/**
 * Parity helpers for Inventory Quick Compare with Page Comparison metrics.
 * Pure functions — no HTTP; matches thresholds/rules documented in PAGE_COMPARISON.md.
 */
import type { QuickCompareTopicFitData } from './quick-compare-topic-fit';
import { formatTopicTier } from './quick-compare-topic-fit';

export interface KeywordOverlapAnalysis {
  sharedKeywords: Array<{
    keyword: string;
    yourPosition: number;
    competitorPosition: number;
    searchVolume?: number;
  }>;
  yourUniqueKeywords: Array<{ keyword: string; position: number; searchVolume?: number }>;
  competitorUniqueKeywords: Array<{ keyword: string; position: number; searchVolume?: number }>;
  sharedCount: number;
  yourUniqueCount: number;
  competitorUniqueCount: number;
  sharedAvgPositionYour: number;
  sharedAvgPositionCompetitor: number;
}

export interface ContentOpportunity {
  keyword: string;
  position: number;
  searchVolume: number;
}

export interface DiagnosisBullet {
  text: string;
  category: 'keywords' | 'links' | 'freshness' | 'content' | 'topicAuthority';
  priority: number;
  winner: 'you' | 'competitor' | 'tie';
}

export function rankedKeywordsNeverFetched(analysis: any): boolean {
  return !analysis?.rankedKeywords;
}

export function getAnalyzedAtIso(analysis: any): string | null {
  if (!analysis?.analyzedAt) return null;
  try {
    const date = new Date(analysis.analyzedAt);
    return date.toISOString();
  } catch {
    return null;
  }
}

export function getLastModifiedIso(analysis: any): string | null {
  if (analysis?.lastContentModified) {
    try {
      const date = new Date(analysis.lastContentModified);
      return date.toISOString();
    } catch {
      // fall through
    }
  }

  const lastModified = analysis?.rawData?.pageData?.last_modified;
  if (!lastModified) return null;

  const timestamp = lastModified.meta_tag || lastModified.sitemap || lastModified.header;
  if (!timestamp) return null;

  try {
    const isoString = timestamp.replace(' ', 'T').replace(' +00:00', 'Z').replace(' -00:00', 'Z');
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) {
      return new Date(timestamp).toISOString();
    }
    return date.toISOString();
  } catch {
    return null;
  }
}

export function getCriticalIssues(analysis: any): string[] {
  return analysis?.criticalIssues || [];
}

export function getRankedKeywordsSorted(analysis: any): any[] {
  const keywords = analysis?.rankedKeywords?.keywords || [];
  return [...keywords].sort((a, b) => (a.position || 999) - (b.position || 999));
}

export function getWordCount(analysis: any): number | null {
  return analysis?.wordCount ?? null;
}

export function getLinkStatsFromAnalysis(analysis: any): {
  total: number;
  contextual: number;
  structural: number;
  samePage: number;
  unknown: number;
} {
  const links = analysis?.links?.internalLinks || [];
  return {
    total: links.length,
    contextual: links.filter((l: any) => l.linkType === 'contextual').length,
    structural: links.filter((l: any) => l.linkType === 'structural').length,
    samePage: links.filter((l: any) => l.linkType === 'same_page').length,
    unknown: links.filter((l: any) => !l.linkType || l.linkType === 'unknown').length
  };
}

/** Overlap when at least one page has a rankedKeywords document; empty lists if fetched but no keywords. */
export function computeKeywordOverlapFromAnalyses(
  yourAnalysis: any,
  competitorAnalysis: any
): KeywordOverlapAnalysis | null {
  const yourFetched = !!yourAnalysis?.rankedKeywords;
  const competitorFetched = !!competitorAnalysis?.rankedKeywords;
  if (!yourFetched && !competitorFetched) {
    return null;
  }

  const yourKeywords = getRankedKeywordsSorted(yourAnalysis);
  const competitorKeywords = getRankedKeywordsSorted(competitorAnalysis);

  const yourKeywordMap = new Map<string, any>();
  yourKeywords.forEach((k: any) => {
    const normalized = k.keyword?.toLowerCase().trim();
    if (normalized) yourKeywordMap.set(normalized, k);
  });

  const competitorKeywordMap = new Map<string, any>();
  competitorKeywords.forEach((k: any) => {
    const normalized = k.keyword?.toLowerCase().trim();
    if (normalized) competitorKeywordMap.set(normalized, k);
  });

  const sharedKeywords: KeywordOverlapAnalysis['sharedKeywords'] = [];
  const yourUniqueKeywords: KeywordOverlapAnalysis['yourUniqueKeywords'] = [];
  const competitorUniqueKeywords: KeywordOverlapAnalysis['competitorUniqueKeywords'] = [];

  yourKeywordMap.forEach((yourKw, normalized) => {
    const competitorKw = competitorKeywordMap.get(normalized);
    if (competitorKw) {
      sharedKeywords.push({
        keyword: yourKw.keyword,
        yourPosition: yourKw.position || 999,
        competitorPosition: competitorKw.position || 999,
        searchVolume: yourKw.searchVolume || competitorKw.searchVolume
      });
    } else {
      yourUniqueKeywords.push({
        keyword: yourKw.keyword,
        position: yourKw.position || 999,
        searchVolume: yourKw.searchVolume
      });
    }
  });

  competitorKeywordMap.forEach((competitorKw, normalized) => {
    if (!yourKeywordMap.has(normalized)) {
      competitorUniqueKeywords.push({
        keyword: competitorKw.keyword,
        position: competitorKw.position || 999,
        searchVolume: competitorKw.searchVolume
      });
    }
  });

  const sharedAvgPositionYour =
    sharedKeywords.length > 0
      ? sharedKeywords.reduce((sum, k) => sum + k.yourPosition, 0) / sharedKeywords.length
      : 0;
  const sharedAvgPositionCompetitor =
    sharedKeywords.length > 0
      ? sharedKeywords.reduce((sum, k) => sum + k.competitorPosition, 0) / sharedKeywords.length
      : 0;

  return {
    sharedKeywords,
    yourUniqueKeywords,
    competitorUniqueKeywords,
    sharedCount: sharedKeywords.length,
    yourUniqueCount: yourUniqueKeywords.length,
    competitorUniqueCount: competitorUniqueKeywords.length,
    sharedAvgPositionYour: Math.round(sharedAvgPositionYour * 10) / 10,
    sharedAvgPositionCompetitor: Math.round(sharedAvgPositionCompetitor * 10) / 10
  };
}

export function getContentOpportunitiesFromOverlap(
  overlap: KeywordOverlapAnalysis | null
): ContentOpportunity[] {
  if (!overlap) return [];
  return overlap.competitorUniqueKeywords
    .filter((k) => (k.searchVolume || 0) >= 100 && k.position <= 20)
    .map((k) => ({
      keyword: k.keyword,
      position: k.position,
      searchVolume: k.searchVolume || 0
    }))
    .sort((a, b) => b.searchVolume - a.searchVolume)
    .slice(0, 10);
}

function getMonthsAgoLabel(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 30) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  }

  const diffMonths = Math.floor(diffDays / 30);
  return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
}

function analyzeKeywordAdvantage(overlap: KeywordOverlapAnalysis | null): DiagnosisBullet[] {
  const bullets: DiagnosisBullet[] = [];
  if (!overlap) return bullets;

  if (overlap.sharedCount >= 3) {
    const positionDiff = overlap.sharedAvgPositionYour - overlap.sharedAvgPositionCompetitor;
    if (positionDiff >= 3) {
      bullets.push({
        text: `They rank better on shared keywords (avg pos ${overlap.sharedAvgPositionCompetitor.toFixed(1)} vs your ${overlap.sharedAvgPositionYour.toFixed(1)}).`,
        category: 'keywords',
        priority: 1,
        winner: 'competitor'
      });
    } else if (positionDiff <= -3) {
      bullets.push({
        text: `You rank better on shared keywords (avg pos ${overlap.sharedAvgPositionYour.toFixed(1)} vs their ${overlap.sharedAvgPositionCompetitor.toFixed(1)}).`,
        category: 'keywords',
        priority: 1,
        winner: 'you'
      });
    }
  }

  const yourUnique = overlap.yourUniqueCount;
  const competitorUnique = overlap.competitorUniqueCount;

  if (competitorUnique >= 5 && yourUnique >= 1) {
    const ratio = competitorUnique / yourUnique;
    if (ratio >= 1.5) {
      bullets.push({
        text: `They rank for more unique keywords (${competitorUnique} vs ${yourUnique}).`,
        category: 'keywords',
        priority: 2,
        winner: 'competitor'
      });
    }
  }

  if (yourUnique >= 5 && competitorUnique >= 1) {
    const ratio = yourUnique / competitorUnique;
    if (ratio >= 1.5) {
      bullets.push({
        text: `You rank for more unique keywords (${yourUnique} vs ${competitorUnique}).`,
        category: 'keywords',
        priority: 2,
        winner: 'you'
      });
    }
  }

  return bullets;
}

function analyzeLinkAdvantage(
  yourIncoming: { contextual: number },
  competitorIncoming: { contextual: number },
  yourOutgoing: { contextual: number },
  competitorOutgoing: { contextual: number }
): DiagnosisBullet[] {
  const bullets: DiagnosisBullet[] = [];

  if (yourIncoming.contextual > 0 || competitorIncoming.contextual > 0) {
    const yc = yourIncoming.contextual;
    const cc = competitorIncoming.contextual;

    if (cc >= 3 && yc >= 1) {
      const ratio = cc / yc;
      if (ratio >= 1.5) {
        bullets.push({
          text: `They have more contextual incoming links (${cc} vs ${yc}).`,
          category: 'links',
          priority: 1,
          winner: 'competitor'
        });
      }
    } else if (cc >= 3 && yc === 0) {
      bullets.push({
        text: `They have contextual incoming links (${cc}) while you have none.`,
        category: 'links',
        priority: 1,
        winner: 'competitor'
      });
    }

    if (yc >= 3 && cc >= 1) {
      const ratio = yc / cc;
      if (ratio >= 1.5) {
        bullets.push({
          text: `You have more contextual incoming links (${yc} vs ${cc}).`,
          category: 'links',
          priority: 1,
          winner: 'you'
        });
      }
    } else if (yc >= 3 && cc === 0) {
      bullets.push({
        text: `You have contextual incoming links (${yc}) while they have none.`,
        category: 'links',
        priority: 1,
        winner: 'you'
      });
    }
  }

  const yo = yourOutgoing.contextual;
  const co = competitorOutgoing.contextual;

  if (co >= 5 && yo >= 1) {
    const ratio = co / yo;
    if (ratio >= 1.5) {
      bullets.push({
        text: `They link out to more related content (${co} vs ${yo} contextual links).`,
        category: 'links',
        priority: 3,
        winner: 'competitor'
      });
    }
  }

  if (yo >= 5 && co >= 1) {
    const ratio = yo / co;
    if (ratio >= 1.5) {
      bullets.push({
        text: `You link out to more related content (${yo} vs ${co} contextual links).`,
        category: 'links',
        priority: 3,
        winner: 'you'
      });
    }
  }

  return bullets;
}

function analyzeFreshnessAdvantage(
  yourLastModifiedIso: string | null,
  competitorLastModifiedIso: string | null
): DiagnosisBullet[] {
  const bullets: DiagnosisBullet[] = [];
  if (!yourLastModifiedIso || !competitorLastModifiedIso) return bullets;

  const yourDate = new Date(yourLastModifiedIso);
  const competitorDate = new Date(competitorLastModifiedIso);
  const diffMs = competitorDate.getTime() - yourDate.getTime();
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30);

  if (diffMonths >= 3) {
    bullets.push({
      text: `Their content is fresher (updated ${getMonthsAgoLabel(competitorDate)} vs your ${getMonthsAgoLabel(yourDate)}).`,
      category: 'freshness',
      priority: 2,
      winner: 'competitor'
    });
  } else if (diffMonths <= -3) {
    bullets.push({
      text: `Your content is fresher (updated ${getMonthsAgoLabel(yourDate)} vs their ${getMonthsAgoLabel(competitorDate)}).`,
      category: 'freshness',
      priority: 2,
      winner: 'you'
    });
  }

  return bullets;
}

function analyzeContentAdvantage(yourWordCount: number, competitorWordCount: number): DiagnosisBullet[] {
  const bullets: DiagnosisBullet[] = [];
  if (yourWordCount >= 200 && competitorWordCount >= 200) {
    if (competitorWordCount > yourWordCount) {
      const ratio = competitorWordCount / yourWordCount;
      if (ratio >= 1.3) {
        bullets.push({
          text: `Their page has more comprehensive content (${competitorWordCount.toLocaleString()} vs ${yourWordCount.toLocaleString()} words).`,
          category: 'content',
          priority: 3,
          winner: 'competitor'
        });
      }
    } else if (yourWordCount > competitorWordCount) {
      const ratio = yourWordCount / competitorWordCount;
      if (ratio >= 1.3) {
        bullets.push({
          text: `Your page has more comprehensive content (${yourWordCount.toLocaleString()} vs ${competitorWordCount.toLocaleString()} words).`,
          category: 'content',
          priority: 3,
          winner: 'you'
        });
      }
    }
  }
  return bullets;
}

function analyzeTopicAuthorityAdvantage(
  yourTopicFit: QuickCompareTopicFitData,
  competitorTopicFit: QuickCompareTopicFitData
): DiagnosisBullet[] {
  const bullets: DiagnosisBullet[] = [];

  if (!yourTopicFit.hasData || !competitorTopicFit.hasData) return bullets;
  if (!yourTopicFit.topicFound || !competitorTopicFit.topicFound) return bullets;
  if (yourTopicFit.authorityScore === null || competitorTopicFit.authorityScore === null) return bullets;

  const yourScore = yourTopicFit.authorityScore;
  const competitorScore = competitorTopicFit.authorityScore;
  const yourTier = yourTopicFit.tier;
  const competitorTier = competitorTopicFit.tier;

  const tierRanking = ['unclassified', 'anti', 'adjacent', 'ambient', 'aspirational', 'owned'];
  const yourTierIndex = yourTier ? tierRanking.indexOf(yourTier) : -1;
  const competitorTierIndex = competitorTier ? tierRanking.indexOf(competitorTier) : -1;

  const sameMainTopic = yourTopicFit.mainTopicSlug === competitorTopicFit.mainTopicSlug;

  if (sameMainTopic) {
    const topicLabel = yourTopicFit.mainTopicLabel;

    if (competitorTierIndex > yourTierIndex && competitorTierIndex >= 4) {
      bullets.push({
        text: `Their domain has stronger authority on "${topicLabel}" (${formatTopicTier(competitorTier)} vs ${formatTopicTier(yourTier)}).`,
        category: 'topicAuthority',
        priority: 1.5,
        winner: 'competitor'
      });
    } else if (yourTierIndex > competitorTierIndex && yourTierIndex >= 4) {
      bullets.push({
        text: `Your domain has stronger authority on "${topicLabel}" (${formatTopicTier(yourTier)} vs ${formatTopicTier(competitorTier)}).`,
        category: 'topicAuthority',
        priority: 1.5,
        winner: 'you'
      });
    } else if (yourTierIndex === competitorTierIndex && yourTierIndex >= 3) {
      const scoreDiff = competitorScore - yourScore;
      if (scoreDiff >= 15) {
        bullets.push({
          text: `Their domain has higher authority on "${topicLabel}" (${competitorScore} vs ${yourScore}).`,
          category: 'topicAuthority',
          priority: 2,
          winner: 'competitor'
        });
      } else if (scoreDiff <= -15) {
        bullets.push({
          text: `Your domain has higher authority on "${topicLabel}" (${yourScore} vs ${competitorScore}).`,
          category: 'topicAuthority',
          priority: 2,
          winner: 'you'
        });
      }
    }
  } else {
    const yourTopicLabel = yourTopicFit.mainTopicLabel;
    const competitorTopicLabel = competitorTopicFit.mainTopicLabel;

    if (competitorTierIndex > yourTierIndex && competitorTierIndex >= 4) {
      bullets.push({
        text: `Their domain is ${formatTopicTier(competitorTier)} on "${competitorTopicLabel}" while yours is ${formatTopicTier(yourTier)} on "${yourTopicLabel}".`,
        category: 'topicAuthority',
        priority: 1.5,
        winner: 'competitor'
      });
    } else if (yourTierIndex > competitorTierIndex && yourTierIndex >= 4) {
      bullets.push({
        text: `Your domain is ${formatTopicTier(yourTier)} on "${yourTopicLabel}" while theirs is ${formatTopicTier(competitorTier)} on "${competitorTopicLabel}".`,
        category: 'topicAuthority',
        priority: 1.5,
        winner: 'you'
      });
    } else if (yourTierIndex === competitorTierIndex && yourTierIndex >= 3) {
      const scoreDiff = competitorScore - yourScore;
      if (scoreDiff >= 15) {
        bullets.push({
          text: `Their domain scores higher on their topic (${competitorScore} on "${competitorTopicLabel}") vs yours (${yourScore} on "${yourTopicLabel}").`,
          category: 'topicAuthority',
          priority: 2,
          winner: 'competitor'
        });
      } else if (scoreDiff <= -15) {
        bullets.push({
          text: `Your domain scores higher on your topic (${yourScore} on "${yourTopicLabel}") vs theirs (${competitorScore} on "${competitorTopicLabel}").`,
          category: 'topicAuthority',
          priority: 2,
          winner: 'you'
        });
      }
    }
  }

  return bullets;
}

export interface QuickCompareDiagnosisInput {
  /** primary (your) analysis */
  yourAnalysis: any;
  /** compare (competitor) analysis */
  competitorAnalysis: any;
  overlap: KeywordOverlapAnalysis | null;
  yourIncoming: { contextual: number };
  competitorIncoming: { contextual: number };
  yourTopicFit: QuickCompareTopicFitData;
  competitorTopicFit: QuickCompareTopicFitData;
}

export function buildQuickCompareDiagnosisBullets(input: QuickCompareDiagnosisInput): DiagnosisBullet[] {
  const {
    yourAnalysis,
    competitorAnalysis,
    overlap,
    yourIncoming,
    competitorIncoming,
    yourTopicFit,
    competitorTopicFit
  } = input;

  if (!yourAnalysis || !competitorAnalysis) return [];

  const bullets: DiagnosisBullet[] = [];
  bullets.push(...analyzeKeywordAdvantage(overlap));
  bullets.push(
    ...analyzeLinkAdvantage(
      yourIncoming,
      competitorIncoming,
      getLinkStatsFromAnalysis(yourAnalysis),
      getLinkStatsFromAnalysis(competitorAnalysis)
    )
  );
  bullets.push(
    ...analyzeFreshnessAdvantage(
      getLastModifiedIso(yourAnalysis),
      getLastModifiedIso(competitorAnalysis)
    )
  );
  bullets.push(
    ...analyzeContentAdvantage(
      getWordCount(yourAnalysis) || 0,
      getWordCount(competitorAnalysis) || 0
    )
  );
  bullets.push(...analyzeTopicAuthorityAdvantage(yourTopicFit, competitorTopicFit));

  bullets.sort((a, b) => a.priority - b.priority);
  return bullets;
}

export function diagnosisHasCompetitorWins(bullets: DiagnosisBullet[]): boolean {
  return bullets.some((b) => b.winner === 'competitor');
}

export function diagnosisHasYourWins(bullets: DiagnosisBullet[]): boolean {
  return bullets.some((b) => b.winner === 'you');
}
